'use client'

import React, { useEffect, useMemo } from 'react'
import Link from 'next/link'
import { Check, X } from 'lucide-react'
import { PhiMask } from '@/components/PhiMask'
import { FormButton, FormLabel, FormSelect } from '@/components/ui/forms'
import { Input } from '@/components/ui/input'
import type { QuestionnaireDTOType } from '@/types/portal-api.generated'
import { cn } from '@/utilities/ui'
import {
  EMPTY_QUESTIONNAIRE_VALIDATION,
  getInitialQuestionnaireAnswers,
  getVisiblePages,
  isErrorContentQuestion,
  isQuestionRequired,
  validateQuestionnaireAnswers,
} from './questionnaireLogic'
import type {
  QuestionDefinition,
  QuestionRadioVariant,
  QuestionnaireAnswerValue,
  QuestionnaireFieldRenderer,
  QuestionnaireFieldRenderers,
  QuestionnaireFormAppearance,
  QuestionnaireFormProps,
} from './types'

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

const EMPTY_QUESTIONNAIRE_CONTEXT = {}
const DEFAULT_ID_PREFIX = 'questionnaire'

type QuestionnaireAppearanceClasses = {
  label: string
  input: string
  selectedOption: string
  unselectedOption: string
  selectedInlineOption: string
  unselectedInlineOption: string
  inlineOptionFocusRing: string
  controlInput: string
  selectLabel: string
  select: string
  content: string
  contentDescription: string
  contentLink: string
  title: string
  pageTitle: string
  controlAccentStyle: React.CSSProperties
}

const QUESTIONNAIRE_APPEARANCE_CLASSES: Record<
  QuestionnaireFormAppearance,
  QuestionnaireAppearanceClasses
> = {
  tenant: {
    label: 'text-tenant-card-foreground!',
    input:
      'border-tenant-border input-placeholder bg-tenant-card-background text-tenant-card-foreground focus-visible:border-tenant-primary-action-color',
    selectedOption:
      'border-tenant-primary-action-color bg-tenant-subtle-accent-background text-tenant-subtle-accent-foreground',
    unselectedOption: 'border-tenant-border bg-tenant-card-background text-tenant-card-foreground',
    selectedInlineOption: 'bg-tenant-primary-action-color text-tenant-primary-action-foreground',
    unselectedInlineOption: 'bg-tenant-subtle-accent-background text-tenant-default-text-color',
    inlineOptionFocusRing:
      'has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-tenant-primary-action-color has-[:focus-visible]:ring-offset-1',
    controlInput:
      'border-tenant-border bg-tenant-card-background text-tenant-primary-action-color accent-tenant-primary-action-color checked:border-transparent checked:bg-current focus:ring-tenant-primary-action-color',
    selectLabel: 'text-base! text-tenant-card-foreground! font-normal',
    select:
      'h-[50px] rounded-lg border-tenant-border bg-tenant-card-background text-tenant-card-foreground focus:border-tenant-primary-action-color focus:ring-tenant-primary-action-color',
    content: 'text-tenant-card-foreground',
    contentDescription: 'text-tenant-description-text',
    contentLink: 'text-tenant-primary-action-color underline hover:underline',
    title: 'text-tenant-card-foreground',
    pageTitle: 'text-tenant-card-foreground',
    controlAccentStyle: {
      accentColor: 'var(--tenant-primary-action-color)',
    },
  },
  client: {
    label: 'text-cp-dark-blue!',
    input:
      'border-cp-light-grey input-placeholder bg-white text-cp-dark-blue focus-visible:border-cp-bright-blue',
    selectedOption: 'border-cp-bright-blue bg-cp-light-purple text-cp-dark-blue',
    unselectedOption: 'border-cp-light-grey bg-white text-cp-dark-blue',
    selectedInlineOption: 'bg-cp-bright-blue text-white',
    unselectedInlineOption: 'bg-cp-light-purple text-cp-dark-blue',
    inlineOptionFocusRing:
      'has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-cp-bright-blue has-[:focus-visible]:ring-offset-1',
    controlInput:
      'border-cp-light-grey bg-white text-cp-bright-blue accent-cp-bright-blue checked:border-transparent checked:bg-current focus:ring-cp-bright-blue',
    selectLabel: 'text-base! text-cp-dark-blue! font-normal',
    select:
      'h-[50px] rounded-lg border-cp-light-grey bg-white text-cp-dark-blue focus:border-cp-bright-blue focus:ring-cp-bright-blue',
    content: 'text-cp-dark-blue',
    contentDescription: 'text-cp-dark-blue',
    contentLink: 'text-cp-bright-blue underline hover:underline',
    title: 'text-cp-dark-blue',
    pageTitle: 'text-cp-dark-blue',
    controlAccentStyle: {
      accentColor: '#4A37FA',
    },
  },
}

function answerAsString(value: QuestionnaireAnswerValue | undefined): string {
  if (value == null || Array.isArray(value)) return ''
  return String(value)
}

function optionsForQuestion(question: Parameters<QuestionnaireFieldRenderer>[0]['question']) {
  if (question.options?.length) return question.options
  if (question.type === 'boolean') return YES_NO_OPTIONS
  return []
}

function questionAriaDescribedBy({
  questionId,
  description,
  fieldId,
  showError,
  error,
}: {
  questionId: string
  description?: string | null
  fieldId: string
  showError?: boolean
  error?: string
}): string | undefined {
  return (
    [
      description?.trim() ? `${questionId}-description` : null,
      showError && error ? `${fieldId}-error` : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined
  )
}

const TextFieldRenderer: QuestionnaireFieldRenderer = ({
  question,
  id,
  appearance,
  labelClassName,
  value,
  required,
  error,
  showError,
  disabled,
  readOnly,
  onChange,
  onBlur,
}) => {
  const appearanceClasses = QUESTIONNAIRE_APPEARANCE_CLASSES[appearance]
  const inputType =
    question.type === 'number'
      ? 'number'
      : question.type === 'tel'
        ? 'tel'
        : question.type === 'date'
          ? 'date'
          : question.type === 'datetime'
            ? 'datetime-local'
            : 'text'
  const inputMode = question.type === 'number' ? 'decimal' : undefined

  return (
    <div>
      <FormLabel
        htmlFor={id}
        className={cn('mb-2 block text-base', appearanceClasses.label, labelClassName)}
        required={required}
      >
        {question.label}
      </FormLabel>
      <QuestionDescription
        questionId={question.questionId}
        description={question.description}
        appearance={appearance}
      />
      <PhiMask>
        <Input
          id={id}
          type={inputType}
          inputMode={inputMode}
          value={answerAsString(value)}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          readOnly={readOnly}
          className={cn(
            'h-12.5 rounded-lg',
            'focus-visible:ring-0 focus-visible:ring-offset-0',
            appearanceClasses.input,
            showError && error && 'border-error-primary focus-visible:border-error-primary',
          )}
          aria-invalid={Boolean(showError && error)}
          aria-describedby={questionAriaDescribedBy({
            questionId: question.questionId,
            description: question.description,
            fieldId: id,
            showError,
            error,
          })}
        />
      </PhiMask>
      <QuestionError id={`${id}-error`} error={showError ? error : undefined} />
    </div>
  )
}

const DateFieldRenderer: QuestionnaireFieldRenderer = (props) => <TextFieldRenderer {...props} />

type RadioFieldVariantContext = {
  checked: boolean
  optionCount: number
  appearanceClasses: QuestionnaireAppearanceClasses
  disabled: boolean
  readOnly: boolean
}

type RadioFieldVariant = {
  fieldsetClassName?: string
  getGroupClassName: (optionCount: number) => string
  getOptionLabelClassName: (context: RadioFieldVariantContext) => string
  getInputClassName: (appearanceClasses: QuestionnaireAppearanceClasses) => string
  getInputStyle?: (appearanceClasses: QuestionnaireAppearanceClasses) => React.CSSProperties
  optionTextClassName?: string
}

function createRadioFieldRenderer(
  variant: RadioFieldVariant,
  displayName: string,
): QuestionnaireFieldRenderer {
  const RadioFieldRenderer: QuestionnaireFieldRenderer = ({
    question,
    id,
    appearance,
    labelClassName,
    value,
    required,
    error,
    showError,
    disabled,
    readOnly,
    onChange,
    onBlur,
  }) => {
    const appearanceClasses = QUESTIONNAIRE_APPEARANCE_CLASSES[appearance]
    const selectedValue = answerAsString(value)
    const options = optionsForQuestion(question)
    const firstOptionId = `${id}-${options[0]?.value ?? 'option'}`
    const optionCount = options.length
    const inputStyle = variant.getInputStyle?.(appearanceClasses)

    return (
      <fieldset
        className={variant.fieldsetClassName}
        aria-describedby={questionAriaDescribedBy({
          questionId: question.questionId,
          description: question.description,
          fieldId: id,
          showError,
          error,
        })}
      >
        <FormLabel
          id={`${id}-label`}
          htmlFor={firstOptionId}
          className={cn('mb-2 block text-base', appearanceClasses.label, labelClassName)}
          required={required}
        >
          {question.label}
        </FormLabel>
        <QuestionDescription
          questionId={question.questionId}
          description={question.description}
          appearance={appearance}
        />
        <div
          className={variant.getGroupClassName(optionCount)}
          role="radiogroup"
          aria-labelledby={`${id}-label`}
        >
          {options.map((option) => {
            const optionId = `${id}-${option.value}`
            const checked = selectedValue === option.value
            return (
              <label
                key={option.value}
                htmlFor={optionId}
                className={variant.getOptionLabelClassName({
                  checked,
                  optionCount,
                  appearanceClasses,
                  disabled: Boolean(disabled),
                  readOnly: Boolean(readOnly),
                })}
                data-testid={`${question.questionId}-${option.value}`}
              >
                <input
                  type="radio"
                  id={optionId}
                  name={id}
                  value={option.value}
                  checked={checked}
                  onChange={(event) => onChange(event.target.value)}
                  onBlur={onBlur}
                  disabled={disabled || readOnly}
                  className={variant.getInputClassName(appearanceClasses)}
                  style={inputStyle}
                />
                {checked ? (
                  <PhiMask as="span" className={variant.optionTextClassName}>
                    {option.label}
                  </PhiMask>
                ) : (
                  <span className={variant.optionTextClassName}>{option.label}</span>
                )}
              </label>
            )
          })}
        </div>
        <QuestionError id={`${id}-error`} error={showError ? error : undefined} />
      </fieldset>
    )
  }
  ;(RadioFieldRenderer as QuestionnaireFieldRenderer & { displayName: string }).displayName =
    displayName
  return RadioFieldRenderer
}

const STACKED_RADIO_VARIANT: RadioFieldVariant = {
  getGroupClassName: () => 'flex flex-col gap-3 sm:flex-row sm:flex-wrap',
  getOptionLabelClassName: ({ checked, appearanceClasses, disabled, readOnly }) =>
    cn(
      'flex min-w-[140px] cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-colors',
      checked ? appearanceClasses.selectedOption : appearanceClasses.unselectedOption,
      disabled || readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    ),
  getInputClassName: (appearanceClasses) =>
    cn('h-4 w-4 focus:ring-2', appearanceClasses.controlInput),
  getInputStyle: (appearanceClasses) => appearanceClasses.controlAccentStyle,
  optionTextClassName: 'text-sm md:text-base',
}

const INLINE_RADIO_VARIANT: RadioFieldVariant = {
  fieldsetClassName: 'm-0 min-w-0 border-0 p-0',
  getGroupClassName: (optionCount) =>
    cn('flex flex-wrap gap-1.5', optionCount === 2 && 'w-full max-w-[10rem]'),
  getOptionLabelClassName: ({ checked, optionCount, appearanceClasses, disabled, readOnly }) =>
    cn(
      'inline-flex h-9 min-h-9 items-center justify-center rounded-full px-3 text-center text-sm leading-tight transition-colors md:h-8 md:min-h-8 md:text-base',
      appearanceClasses.inlineOptionFocusRing,
      optionCount === 2 && 'min-w-0 flex-1',
      checked ? appearanceClasses.selectedInlineOption : appearanceClasses.unselectedInlineOption,
      disabled || readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    ),
  getInputClassName: () => 'sr-only',
}

const INLINE_SPACED_RADIO_VARIANT: RadioFieldVariant = {
  fieldsetClassName: 'm-0 min-w-0 border-0 p-0',
  getGroupClassName: (optionCount) =>
    cn(
      'flex flex-wrap items-center gap-y-3',
      optionCount === 2 ? 'gap-x-32 md:gap-x-40' : 'gap-x-12 md:gap-x-16',
    ),
  getOptionLabelClassName: ({ disabled, readOnly }) =>
    cn(
      'inline-flex cursor-pointer items-center gap-3',
      disabled || readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    ),
  getInputClassName: (appearanceClasses) =>
    cn('h-4 w-4 shrink-0 focus:ring-2', appearanceClasses.controlInput),
  getInputStyle: (appearanceClasses) => appearanceClasses.controlAccentStyle,
  optionTextClassName: 'text-sm md:text-base',
}

const StackedRadioFieldRenderer = createRadioFieldRenderer(
  STACKED_RADIO_VARIANT,
  'StackedRadioFieldRenderer',
)
const InlineRadioFieldRenderer = createRadioFieldRenderer(
  INLINE_RADIO_VARIANT,
  'InlineRadioFieldRenderer',
)
const InlineSpacedRadioFieldRenderer = createRadioFieldRenderer(
  INLINE_SPACED_RADIO_VARIANT,
  'InlineSpacedRadioFieldRenderer',
)

const SelectFieldRenderer: QuestionnaireFieldRenderer = ({
  question,
  id,
  appearance,
  labelClassName,
  value,
  required,
  error,
  showError,
  disabled,
  readOnly,
  onChange,
  onBlur,
}) => {
  const appearanceClasses = QUESTIONNAIRE_APPEARANCE_CLASSES[appearance]

  return (
    <div>
      <FormLabel
        htmlFor={id}
        className={cn('mb-2 block text-base', appearanceClasses.selectLabel, labelClassName)}
        required={required}
      >
        {question.label}
      </FormLabel>
      <QuestionDescription
        questionId={question.questionId}
        description={question.description}
        appearance={appearance}
      />
      <FormSelect
        id={id}
        value={answerAsString(value)}
        onChange={onChange}
        onBlur={onBlur}
        options={optionsForQuestion(question)}
        placeholder="Select an option"
        required={required}
        disabled={disabled || readOnly}
        error={showError ? error : undefined}
        selectClassName={appearanceClasses.select}
        maskSelectedValue
        ariaDescribedBy={questionAriaDescribedBy({
          questionId: question.questionId,
          description: question.description,
          fieldId: id,
          showError,
          error,
        })}
        fullWidth
      />
    </div>
  )
}

const MultiSelectFieldRenderer: QuestionnaireFieldRenderer = ({
  question,
  id,
  appearance,
  labelClassName,
  value,
  required,
  error,
  showError,
  disabled,
  readOnly,
  onChange,
  onBlur,
}) => {
  const appearanceClasses = QUESTIONNAIRE_APPEARANCE_CLASSES[appearance]
  const selectedValues = Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
  const options = optionsForQuestion(question)
  const firstOptionId = `${id}-${options[0]?.value ?? 'option'}`

  return (
    <fieldset
      aria-describedby={questionAriaDescribedBy({
        questionId: question.questionId,
        description: question.description,
        fieldId: id,
        showError,
        error,
      })}
    >
      <FormLabel
        htmlFor={firstOptionId}
        className={cn('mb-2 block text-base', appearanceClasses.label, labelClassName)}
        required={required}
      >
        {question.label}
      </FormLabel>
      <QuestionDescription
        questionId={question.questionId}
        description={question.description}
        appearance={appearance}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {options.map((option) => {
          const checked = selectedValues.includes(option.value)
          const optionId = `${id}-${option.value}`
          return (
            <label
              key={option.value}
              htmlFor={optionId}
              className={cn(
                'grid min-h-12 grid-cols-[auto_minmax(0,1fr)] items-start gap-3 rounded-lg border px-4 py-3 transition-colors',
                checked ? appearanceClasses.selectedOption : appearanceClasses.unselectedOption,
                disabled || readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              )}
              data-testid={`${question.questionId}-${option.value}`}
            >
              <input
                id={optionId}
                type="checkbox"
                value={option.value}
                checked={checked}
                onChange={(event) => {
                  const nextValues = event.target.checked
                    ? [...selectedValues, option.value]
                    : selectedValues.filter((selectedValue) => selectedValue !== option.value)
                  onChange(nextValues)
                }}
                onBlur={onBlur}
                disabled={disabled || readOnly}
                className={cn(
                  'mt-0.5 h-4 w-4 rounded focus:ring-2',
                  appearanceClasses.controlInput,
                )}
                style={appearanceClasses.controlAccentStyle}
              />
              {checked ? (
                <PhiMask as="span" className="min-w-0 wrap-break-word leading-5">
                  {option.label}
                </PhiMask>
              ) : (
                <span className="min-w-0 wrap-break-word leading-5">{option.label}</span>
              )}
            </label>
          )
        })}
      </div>
      <QuestionError id={`${id}-error`} error={showError ? error : undefined} />
    </fieldset>
  )
}

const EMAIL_PATTERN = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g

function renderTextWithEmailLinks(text: string, linkClassName: string): React.ReactNode {
  const nodes: React.ReactNode[] = []
  let lastIndex = 0

  for (const match of text.matchAll(EMAIL_PATTERN)) {
    const email = match[1]
    const matchIndex = match.index ?? 0

    if (matchIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, matchIndex))
    }

    nodes.push(
      <a key={matchIndex} href={`mailto:${email}`} className={linkClassName}>
        {email}
      </a>,
    )
    lastIndex = matchIndex + match[0].length
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return nodes.length > 0 ? nodes : text
}

const ContentRenderer: QuestionnaireFieldRenderer = ({ question, appearance, labelClassName }) => {
  const appearanceClasses = QUESTIONNAIRE_APPEARANCE_CLASSES[appearance]
  const isErrorVariant = question.variant === 'error'
  const isConfirmEligibleVariant = question.variant === 'confirm_eligible'
  const isContentVariant = isErrorVariant || isConfirmEligibleVariant
  const hasDescription = Boolean(question.description?.trim())
  const usesRichContentLayout = isContentVariant || hasDescription

  if (usesRichContentLayout) {
    return (
      <div data-testid={`${question.questionId}-content`}>
        {isErrorVariant ? (
          <div className="py-5" data-testid={`${question.questionId}-content-error-icon`}>
            <div
              className={cn(
                'flex items-center justify-center rounded-full bg-warning-fill-dark',
                isContentVariant ? 'h-6 w-6' : 'h-4.5 w-4.5',
              )}
              aria-hidden="true"
            >
              <X
                className={cn('text-white', isContentVariant ? 'h-5 w-5' : 'size-3.75')}
                strokeWidth={2.5}
              />
            </div>
          </div>
        ) : null}
        {isConfirmEligibleVariant ? (
          <div
            className="py-5"
            data-testid={`${question.questionId}-content-confirm-eligible-icon`}
          >
            <div
              className={cn(
                'flex items-center justify-center rounded-full bg-success-fill',
                isContentVariant ? 'h-6 w-6' : 'h-4.5 w-4.5',
              )}
              aria-hidden="true"
            >
              <Check
                className={cn('text-white', isContentVariant ? 'h-5 w-5' : 'size-3.75')}
                strokeWidth={2.5}
              />
            </div>
          </div>
        ) : null}
        <p
          className={cn(
            appearanceClasses.content,
            isContentVariant
              ? 'mb-3 text-xl font-medium leading-snug'
              : cn(
                  'text-lg md:text-xl leading-snug',
                  usesRichContentLayout ? cn(labelClassName, 'mb-3 font-medium') : labelClassName,
                ),
          )}
        >
          {question.label}
        </p>
        {hasDescription ? (
          <p
            className={cn(
              'whitespace-pre-wrap',
              isContentVariant
                ? 'text-base font-normal leading-6'
                : 'text-xs md:text-base font-normal leading-6',
              isContentVariant ? appearanceClasses.content : appearanceClasses.contentDescription,
            )}
            data-testid={`${question.questionId}-content-description`}
          >
            {renderTextWithEmailLinks(question.description!.trim(), appearanceClasses.contentLink)}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <p className={cn('text-sm leading-6', appearanceClasses.content, labelClassName)}>
      {question.label}
    </p>
  )
}

function QuestionError({ id, error }: Readonly<{ id: string; error?: string }>) {
  return (
    <div className="mt-1 min-h-5">
      {error ? (
        <p id={id} className="text-xs text-error-primary" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

function QuestionDescription({
  questionId,
  description,
  appearance,
  className,
}: Readonly<{
  questionId: string
  description?: string | null
  appearance: QuestionnaireFormAppearance
  className?: string
}>) {
  const trimmedDescription = description?.trim()
  if (!trimmedDescription) return null

  const appearanceClasses = QUESTIONNAIRE_APPEARANCE_CLASSES[appearance]
  return (
    <p
      id={`${questionId}-description`}
      className={cn(
        'mb-3 whitespace-pre-wrap text-xs md:text-base font-normal leading-6',
        appearanceClasses.contentDescription,
        className,
      )}
      data-testid={`${questionId}-description`}
    >
      {renderTextWithEmailLinks(trimmedDescription, appearanceClasses.contentLink)}
    </p>
  )
}

const DEFAULT_FIELD_RENDERERS: Record<string, QuestionnaireFieldRenderer> = {
  text: TextFieldRenderer,
  number: TextFieldRenderer,
  tel: TextFieldRenderer,
  address: TextFieldRenderer,
  date: DateFieldRenderer,
  datetime: DateFieldRenderer,
  boolean: StackedRadioFieldRenderer,
  select: SelectFieldRenderer,
  multi_select: MultiSelectFieldRenderer,
  content: ContentRenderer,
}

const RADIO_VARIANT_RENDERERS: Record<QuestionRadioVariant, QuestionnaireFieldRenderer> = {
  stacked: StackedRadioFieldRenderer,
  inline: InlineRadioFieldRenderer,
  inline_spaced: InlineSpacedRadioFieldRenderer,
}

function getRadioVariant(question: QuestionDefinition): QuestionRadioVariant {
  const { variant } = question
  if (variant === 'inline' || variant === 'inline_spaced' || variant === 'stacked') {
    return variant
  }
  return 'stacked'
}

function resolveFieldRenderer(
  question: QuestionDefinition,
  fieldRenderers?: QuestionnaireFieldRenderers,
): QuestionnaireFieldRenderer {
  if (question.type === 'radio') {
    const variant = getRadioVariant(question)
    return fieldRenderers?.radio ?? RADIO_VARIANT_RENDERERS[variant]
  }

  return (
    fieldRenderers?.[question.type] ?? DEFAULT_FIELD_RENDERERS[question.type] ?? TextFieldRenderer
  )
}

function areAnswersEqual(
  left: Record<string, QuestionnaireAnswerValue>,
  right: Record<string, QuestionnaireAnswerValue>,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

type RenderedQuestionnairePage = QuestionnaireDTOType['pages'][number]

type QuestionnaireQuestionProps = {
  question: QuestionDefinition
  answers: QuestionnaireFormProps['answers']
  appearance: QuestionnaireFormAppearance
  disabled: boolean
  fieldRenderers?: QuestionnaireFieldRenderers
  idPrefix: string
  labelClassName?: string
  onAnswersChange: QuestionnaireFormProps['onAnswersChange']
  onQuestionBlur?: QuestionnaireFormProps['onQuestionBlur']
  readOnly: boolean
  required: boolean
  error?: string
  showErrors: boolean
}

function QuestionnaireQuestion({
  question,
  answers,
  appearance,
  disabled,
  fieldRenderers,
  idPrefix,
  labelClassName,
  onAnswersChange,
  onQuestionBlur,
  readOnly,
  required,
  error,
  showErrors,
}: Readonly<QuestionnaireQuestionProps>) {
  const Renderer = resolveFieldRenderer(question, fieldRenderers)
  const isContent = question.type === 'content'

  const handleChange = (value: QuestionnaireAnswerValue) => {
    onAnswersChange((currentAnswers) => ({
      ...currentAnswers,
      [question.questionId]: value,
    }))
  }

  const handleBlur = () => {
    onQuestionBlur?.(question.questionId)
  }

  return (
    <div
      className={cn(isContent && 'lg:col-span-2')}
      data-testid={`questionnaire-question-${question.questionId}`}
    >
      {Renderer({
        question,
        id: `${idPrefix}-${question.questionId}`,
        appearance,
        labelClassName,
        value: answers[question.questionId],
        required,
        error,
        showError: showErrors,
        disabled,
        readOnly,
        onChange: handleChange,
        onBlur: handleBlur,
      })}
    </div>
  )
}

type QuestionnairePageProps = {
  page: RenderedQuestionnairePage
  answers: QuestionnaireFormProps['answers']
  appearance: QuestionnaireFormAppearance
  context: NonNullable<QuestionnaireFormProps['context']>
  disabled: boolean
  fieldRenderers?: QuestionnaireFieldRenderers
  gridClassName?: string
  idPrefix: string
  includePageNames: boolean
  labelClassName?: string
  onAnswersChange: QuestionnaireFormProps['onAnswersChange']
  onQuestionBlur?: QuestionnaireFormProps['onQuestionBlur']
  pageClassName?: string
  readOnly: boolean
  showErrors: boolean
  validation: ReturnType<typeof validateQuestionnaireAnswers>
}

function QuestionnairePage({
  page,
  answers,
  appearance,
  context,
  disabled,
  fieldRenderers,
  gridClassName,
  idPrefix,
  includePageNames,
  labelClassName,
  onAnswersChange,
  onQuestionBlur,
  pageClassName,
  readOnly,
  showErrors,
  validation,
}: Readonly<QuestionnairePageProps>) {
  const appearanceClasses = QUESTIONNAIRE_APPEARANCE_CLASSES[appearance]

  return (
    <div className={cn('space-y-6', pageClassName)}>
      {includePageNames && (
        <h3 className={cn('text-base font-medium', appearanceClasses.pageTitle)}>
          {page.pageName}
        </h3>
      )}

      <div className={gridClassName ?? 'grid grid-cols-1 gap-6 lg:grid-cols-2'}>
        {page.questions.map((question) => (
          <QuestionnaireQuestion
            key={question.questionId}
            question={question}
            answers={answers}
            appearance={appearance}
            disabled={disabled}
            fieldRenderers={fieldRenderers}
            idPrefix={idPrefix}
            labelClassName={labelClassName}
            onAnswersChange={onAnswersChange}
            onQuestionBlur={onQuestionBlur}
            readOnly={readOnly}
            required={isQuestionRequired(question, answers, context)}
            error={validation.errors[question.questionId]}
            showErrors={showErrors}
          />
        ))}
      </div>
    </div>
  )
}

export function QuestionnaireForm({
  questionnaire,
  answers,
  onAnswersChange,
  context = EMPTY_QUESTIONNAIRE_CONTEXT,
  showErrors = false,
  disabled = false,
  readOnly = false,
  className,
  title,
  titleClassName,
  appearance = 'tenant',
  idPrefix = DEFAULT_ID_PREFIX,
  fieldRenderers,
  onValidationChange,
  onQuestionBlur,
  includePageNames = true,
  activePageIndex = 0,
  pageGroupClassName,
  pageClassName,
  gridClassName,
  labelClassName,
  errorContentAction,
  actionsClassName,
}: Readonly<QuestionnaireFormProps>) {
  const appearanceClasses = QUESTIONNAIRE_APPEARANCE_CLASSES[appearance]

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (!questionnaire) return
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- data passed to parent from an effect; keep until refactor
    onAnswersChange((currentAnswers) => {
      const initializedAnswers = getInitialQuestionnaireAnswers(
        questionnaire,
        context,
        currentAnswers,
      )
      return areAnswersEqual(initializedAnswers, currentAnswers)
        ? currentAnswers
        : initializedAnswers
    })
  }, [context, onAnswersChange, questionnaire])

  const validation = useMemo(
    () => validateQuestionnaireAnswers(questionnaire, answers, context),
    [answers, context, questionnaire],
  )

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- data passed to parent from an effect; keep until refactor
    onValidationChange?.(questionnaire ? validation : EMPTY_QUESTIONNAIRE_VALIDATION)
  }, [onValidationChange, questionnaire, validation])

  const renderedPages = useMemo(() => {
    if (!questionnaire) return []
    const visiblePages = getVisiblePages(questionnaire, answers, context)
    if (visiblePages.length === 0) return []

    const boundedIndex = Math.min(Math.max(activePageIndex, 0), visiblePages.length - 1)
    const activePage = visiblePages[boundedIndex]
    return activePage ? [activePage] : []
  }, [activePageIndex, answers, context, questionnaire])

  const hasErrorContent = renderedPages.some((page) =>
    page.questions.some((question) => isErrorContentQuestion(question)),
  )

  if (!questionnaire || renderedPages.length === 0) return null

  return (
    <section className={className}>
      {title && (
        <h2 className={cn('mb-6 text-lg font-medium', appearanceClasses.title, titleClassName)}>
          {title}
        </h2>
      )}

      <div className={cn('space-y-8', pageGroupClassName)}>
        {renderedPages.map((page) => (
          <QuestionnairePage
            key={`${page.pageName}-${page.questions[0]?.questionId ?? 'empty'}`}
            page={page}
            answers={answers}
            appearance={appearance}
            context={context}
            disabled={disabled}
            fieldRenderers={fieldRenderers}
            gridClassName={gridClassName}
            idPrefix={idPrefix}
            includePageNames={includePageNames}
            labelClassName={labelClassName}
            onAnswersChange={onAnswersChange}
            onQuestionBlur={onQuestionBlur}
            pageClassName={pageClassName}
            readOnly={readOnly}
            showErrors={showErrors}
            validation={validation}
          />
        ))}
      </div>

      {hasErrorContent && errorContentAction ? (
        <div
          className={cn('flex justify-center items-center pt-8 md:pt-8 md:pb-12', actionsClassName)}
          data-testid="questionnaire-error-content-actions"
        >
          <FormButton
            asChild
            variant="outline"
            size="lg"
            width="lg"
            aria-label={errorContentAction.ariaLabel ?? errorContentAction.label}
          >
            <Link
              href={errorContentAction.href}
              onClick={errorContentAction.onClick}
              className={cn(
                appearance === 'tenant' && 'text-tenant-primary-action-color!',
                appearance === 'client' && 'text-cp-bright-blue!',
                'no-underline hover:no-underline',
              )}
            >
              {errorContentAction.label}
            </Link>
          </FormButton>
        </div>
      ) : null}
    </section>
  )
}
